import { Box } from '@startuptools/ui/joy/Box';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { Tooltip } from '@startuptools/ui/joy/Tooltip';
import Dangerous from '@mui/icons-material/Dangerous';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { ComponentProps } from 'react';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { useLingui } from '@lingui/react';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { Input } from '@startuptools/ui/joy/Input';

interface EditableFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  value?: string;
  rules?: Omit<RegisterOptions<T, Path<T>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  autoFocus?: boolean;
  isEditing: boolean;
  onEditToggle: (editing: boolean) => void;
  placeholder?: string;
  sx?: ComponentProps<typeof Box>['sx'];
  decimalScale?: ComponentProps<typeof NumericFormatInput>['decimalScale'];
  numeric?: boolean;
}

export function EditableTableCell<T extends FieldValues>({
  name,
  control,
  value,
  rules,
  autoFocus,
  isEditing,
  onEditToggle,
  placeholder,
  sx,
  numeric = false,
  decimalScale,
}: EditableFieldProps<T>) {
  const { i18n } = useLingui();
  const EditComponent = numeric ? NumericFormatInput : Input;
  if (isEditing) {
    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field, fieldState }) => (
          <FormControl error={Boolean(fieldState.error)}>
            <Tooltip title={fieldState.error?.message}>
              <EditComponent
                decimalScale={decimalScale}
                lang={i18n.locale}
                value={field.value ?? ''}
                autoFocus={autoFocus}
                onChange={e => field.onChange(e.target.value)}
                placeholder={placeholder}
                sx={{ width: '100%' }}
                endDecorator={Boolean(fieldState.error) && <Dangerous color="error" />}
              />
            </Tooltip>
          </FormControl>
        )}
      />
    );
  }

  return (
    <Box
      onClick={() => onEditToggle(true)}
      sx={{
        cursor: 'pointer',
        width: '100%',
        minHeight: '35px', // magic number to make cell equal height of input
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...sx,
      }}
    >
      {numeric ? (
        <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
          {value}
        </NumberFormat>
      ) : (
        value
      )}
    </Box>
  );
}
